import { copyText } from './copied'
import { currencySymbol, postcodeWords, roundConfig } from './round-config'
import './write-quote.css'
import './invoices.css'
import { invoiceBreakdown, type BreakdownLine } from './invoice-breakdown'
import { api, apiBlob, ApiError, changeNow, dismiss, getQueue, mutate, type Ack } from './api'
import { isSendPress, leadPhoneLink, wayWords, type NoteChannel } from './contact'
import { wayIcon } from './contact-ui'
import { bindEmailSend } from './email-send'
import { enquiryMessages, enquiryNoteLine } from './enquiry-message'
import { mailtoHref } from './mailto'
import { getMe } from './me'
import { EMAIL_RULE } from '../functions/lib/message-by'
import { jobFromLead, missingWords, parkedOnJob, QUOTE_LABEL, quoteAmount, quoteMessage, quoteSaveFields, quotedStatus, replyOutOfDate, type JobDraft } from './quote-message'
import { appendNote, onAPhone } from './rebook-message'
import { kindLabel, mailSubject } from './trade'
import type { Lead } from './types'
import { cycleLabel, el, money, parseMoney, shortDate, toast, todayISO, noteDate } from './ui'
import { howOftenPicker, oftenKind, type Often, type OftenKind } from './how-often'
import { lineDrawing, type Drawing } from './picture-tiles'
import { offerExtras } from './offer-extras'
import { blankPriceList, windowRate, quote, sizeRates, type HowOften, type Job, type JobKind, type PriceList, type Quote } from '../functions/lib/price-list'

// "Write a quote" on New jobs (quotes by email, step 2; plan docs/plans/next-level/plan.html). The owner
// fills in the job, the price comes from their own price list through quote() (the one price sum), and the
// reply is drafted in src/quote-message.ts, ready to change before it goes. No AI:
// the owner can send it by email through Roundly, or use their own WhatsApp, text or email.
// The quote saves only after a successful send or the owner says it went, with the same messaged marking.
//
// Owner only, like Your prices: workers never reach New jobs, /api/prices refuses them, and the panel
// opens nothing for one. Laptop first: the job and the reply side by side; one column on a phone.

interface LeadQuote { id: number; number: number; revision: number; lines_json: string; note: string; token: string; sent_at: string | null }
let openPanel: HTMLDialogElement | null = null

// How often's weeks to Your prices' bands (27 Oct 2026): 2, 4, 6, 8, 12 weeks; any other number of weeks prices at the
// nearest band (3 weeks as 4, 26 as 12), and books at its own weeks.
const BAND_WEEKS: Record<Exclude<HowOften, 'one_off'>, number> = { w2: 2, w4: 4, w6: 6, w8: 8, w12: 12 }
function bandOf(days: number): HowOften {
  const weeks = days / 7
  return (Object.entries(BAND_WEEKS) as [HowOften, number][]).sort((a, b) => Math.abs(a[1] - weeks) - Math.abs(b[1] - weeks))[0]![0]
}

// The same saved-quote and two-minute claim rules as the send route.
const hasSavedQuote = (lead: Lead): boolean => lead.status === 'quote' || lead.regular_pence != null || lead.initial_pence != null
const liveQuoteSend = (lead: Lead): boolean => !!lead.quote_send_key && !!lead.quote_send_at && lead.quote_send_at > new Date(Date.now() - 120_000).toISOString().slice(0, 19).replace('T', ' ')

interface PricesPayload {
  list: PriceList | null
  version: number
}

// A small labelled field, in the booking dialog's style.
// The visible label is tied to its box by id, and is the box's name (no separate aria-label), so a tap
// on the words focuses the box and a screen reader reads what is on screen.
let fieldSeq = 0
function field(label: string, control: HTMLElement, help?: string): HTMLElement {
  control.id ||= `write-quote-field-${++fieldSeq}`
  control.removeAttribute('aria-label')
  const wrap = el('label', { class: 'booking-field', for: control.id }, label)
  wrap.append(control)
  if (help) wrap.append(el('span', { class: 'field-help' }, help))
  return wrap
}

const wholeOrNull = (v: string): number | null => {
  const n = Math.floor(Number(v))
  return v.trim() && Number.isFinite(n) && n > 0 ? n : null
}

// Lives outside the screen (like the Message dialog) so a background refresh of New jobs cannot erase a
// quote being written. `saved` runs once the quote has saved on the job, so the board can redraw.
export function openWriteQuote(lead: Lead, saved: () => void): void {
  if (getMe()?.role !== 'owner') return
  if (openPanel) { openPanel.focus(); return }
  const previous = document.activeElement as HTMLElement | null
  const dialog = el('dialog', { class: 'booking-dialog write-quote-dialog', 'aria-labelledby': 'write-quote-title' })
  openPanel = dialog
  const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const content = el('div', { class: 'booking-content' }, el('p', {}, 'Loading your prices…'))
  dialog.append(el('header', { class: 'booking-head' }, el('h2', { id: 'write-quote-title' }, `Write a quote · ${lead.name}`), close), content)
  let dirty = false // the owner has changed the reply by hand
  let saving = false
  const canClose = (): boolean => !saving && (!dirty || window.confirm('Close this quote? Changes you have not saved will be lost.'))
  close.addEventListener('click', () => { if (canClose()) dialog.close() })
  dialog.addEventListener('cancel', (e) => { if (!canClose()) e.preventDefault() })
  dialog.addEventListener('close', () => { dialog.remove(); openPanel = null; if (previous?.isConnected) previous.focus() })
  document.body.append(dialog)
  dialog.showModal()

  void (async () => {
    let prices: PricesPayload
    let current = lead
    try {
      // The job fresh from the server, so the save goes on its latest version (as the Message dialog does).
      const [p, l] = await Promise.all([
        api<PricesPayload>('/api/prices'),
        api<{ lead: Lead }>(`/api/leads/${lead.id}`).then((r) => r.lead).catch(() => lead),
      ])
      prices = p
      if (l && l.id === lead.id) current = l
    } catch {
      if (!dialog.isConnected) return
      content.textContent = ''
      content.append(el('p', {}, "Couldn't load your prices. Check your signal and try again."))
      return
    }
    if (!dialog.isConnected) return
    content.textContent = ''
    const list = prices.list
    const oldQuotes = await api<{ rows: LeadQuote[] }>(`/api/leads/${current.id}/quotes`).catch(() => ({ rows: [] }))
    if (!dialog.isConnected) return
    const hooks = {
      edited: (on: boolean) => { dirty = on },
      saving: (on: boolean) => { saving = on; close.disabled = on },
      done: () => { dirty = false; dialog.close(); saved() },
    }
    if (!list) {
      const link = el('a', { href: '#/prices', class: 'btn-primary' }, 'Set your prices')
      link.addEventListener('click', () => dialog.close())
      content.append(el('h3', {}, 'Set your prices first'), link)
      const manual = el('button', { type: 'button', class: 'btn-secondary' }, 'Write a one-off breakdown')
      manual.addEventListener('click', () => {
        content.replaceChildren()
        drawPanel(content, blankPriceList('per_window'), current, hooks, oldQuotes.rows[0], true)
      })
      content.append(manual)
      return
    }
    drawPanel(content, list, current, hooks, oldQuotes.rows[0])
  })()
}

function drawPanel(root: HTMLElement, list: PriceList, opened: Lead, hooks: { edited: (on: boolean) => void; saving: (on: boolean) => void; done: () => void }, oldQuote?: LeadQuote, manualOnly = false): void {
  let outcomeUnknown = false
  let claimTimer: ReturnType<typeof setTimeout> | undefined
  openPanel?.addEventListener('close', () => { if (claimTimer) clearTimeout(claimTimer) })
  let emailOn = false
  let sendIntent: { key: string; body: Record<string, unknown> } | null = null
  void api<{ row: { quotes_by_email: boolean } }>('/api/business/quotes-email').then(r => { emailOn = r.row.quotes_by_email; drawSend() }).catch(() => {})
  let lead = opened // replaced by the latest copy after a clash, so writing it again goes on the newest version
  const job: JobDraft = jobFromLead(lead)
  if (manualOnly) job.how_often = 'one_off'
  // A job priced by its size (15 Oct 2026) is one price for the job: no how often to pick before it shows.
  if (list.method === 'by_area') { job.how_often = job.how_often ?? 'one_off'; job.kind = 'window' }
  let edited = false
  let builtFrom: string | null = null // the reply as last written from the price
  let busy = false // the save is on its way
  let breakdownOpen = false
  let breakdownLines: BreakdownLine[] = oldQuote ? JSON.parse(oldQuote.lines_json) as BreakdownLine[] : []
  const breakdownTotal = (): number => breakdownLines.reduce((n, l) => n + l.count * l.each_pence, 0)
  const breakdownAllowed = (): boolean => oftenK === 'once' || oftenK === 'adhoc'
  const usingBreakdown = (): boolean => breakdownOpen && breakdownAllowed()
  let editor: ReturnType<typeof invoiceBreakdown> | undefined
  let previewNumber = oldQuote?.number
  let issued: { row: LeadQuote; fingerprint: string } | null = null
  let quoteIntent: { key: string; fingerprint: string } | null = null
  let confirmIntent: { key: string; body: string } | null = null
  let previewSetup: { business_name?: string; address?: string | null; contact?: string | null; vat_on?: boolean } = {}
  let channel: NoteChannel = leadPhoneLink(lead.phone, '', { own: lead.message_by }).channel
  // Write a quote B (Liam, 27 Oct 2026: https://claude.ai/artifact/AbMUEzh1Hf55pam4fZiyp6): three steps. What's the job
  // (picture tiles), the house and How often (the app's one question, src/how-often.ts), then the price, every figure a
  // box the owner can type over, and the reply. Windows is always part of a window quote.
  type Svc = 'windows' | 'parking'
  const services = new Set<Svc>(['windows'])
  let often: Often | null = manualOnly ? { days: null, marker: 'Just once' } : job.how_often ? (job.how_often === 'one_off' ? { days: null, marker: (lead as Lead & { cycle_text?: string | null }).cycle_text ?? 'Just once' } : { days: BAND_WEEKS[job.how_often] * 7, marker: null }) : null
  let oftenK: OftenKind | null = oftenKind(often)
  let parkingWords = ''
  const typed: { first?: string; regular?: string } = {}
  let step = 1
  const moneyOf = (t: string | undefined): number | null => { if (t === undefined || !t.trim()) return null; try { return parseMoney(t) } catch { return null } }
  // No price until how often is picked: the enquiry may not say (Astra, 1 Oct). A price typed over the calculator's wins.
  let picker: ReturnType<typeof offerExtras> | undefined
  const priced = (): Quote | null => {
    if (manualOnly) return null
    if (!job.how_often) return null
    const c = quote(list, job as Job, currencySymbol(), roundConfig().country)
    const f = moneyOf(typed.first), r = moneyOf(typed.regular), i = picker?.value().insides?.pence ?? null
    return { ...c, ...(f !== null ? { first_pence: f, first_range_pence: null } : {}), ...(r !== null ? { regular_pence: r, regular_range_pence: null } : {}), ...(i !== null ? { inside_pence: i, inside_range_pence: null } : {}) }
  }
  let q: Quote | null = priced()
  picker = offerExtras({ inside: () => job.how_often ? quote(list, job as Job, currencySymbol(), roundConfig().country).inside_pence : null,
    insideAvailable: list.inside_pct > 0, everyDefault: true, onChange: () => { redraw() } })
  // The same picker keeps its service pills in step 1 and its editable figures in step 3.
  picker.fields.remove()
  const extrasNow = () => picker!.value().items
  // ---- 2. the job: the owner fills it in, with what the enquiry says beside it ----
  const jobBox = el('section', { class: 'write-quote-job' }, el('h3', {}, 'The job'))
  const said = [lead.initial_lead, lead.notes, lead.notes2].map((t) => (t ?? '').trim()).filter(Boolean)
  if (said.length) {
    const box = el('div', { class: 'write-quote-said' }, el('span', { class: 'rebook-fact-label' }, 'What the enquiry says'))
    for (const t of said) box.append(el('p', {}, t))
    jobBox.append(box)
  }
  const fields = el('div', { class: 'booking-fields' })
  if (list.method === 'by_area') {
    // The size and which kind of job; nothing about windows (Liam picked A, 15 Oct 2026).
    const kinds = (['window', 'gutter', 'other'] as const).filter((k) => (sizeRates(list)[k] ?? 0) > 0)
    if (kinds.length && !kinds.includes(job.kind as 'window')) job.kind = kinds[0]!
    const kindSel = el('select', { 'aria-label': 'Which job' })
    for (const k of kinds) kindSel.append(el('option', { value: k }, kindLabel(k)))
    kindSel.value = job.kind ?? 'window'
    kindSel.addEventListener('change', () => { job.kind = kindSel.value as JobKind; redraw() })
    const sqm = el('input', { type: 'number', inputmode: 'decimal', min: '0.1', step: '0.1', 'aria-label': 'Size in square metres' })
    sqm.value = job.sqm != null ? String(job.sqm) : ''
    sqm.addEventListener('input', () => { const n = Number(sqm.value); job.sqm = sqm.value.trim() && Number.isFinite(n) && n > 0 ? n : null; redraw() })
    fields.append(field('Which job', kindSel), field('Size (m²)', sqm))
  } else if (list.method === 'per_window') {
    const windows = el('input', { type: 'number', inputmode: 'numeric', min: '1', step: '1', 'aria-label': 'Number of windows' })
    windows.value = job.windows != null ? String(job.windows) : ''
    windows.addEventListener('input', () => { job.windows = wholeOrNull(windows.value); redraw() })
    fields.append(field('Number of windows', windows))
  } else {
    const size = el('select', { 'aria-label': 'House size' })
    size.append(el('option', { value: '' }, 'Pick a size'))
    for (const s of list.sizes.filter((x) => x.pence > 0)) size.append(el('option', { value: s.label }, s.label))
    size.value = job.size ?? ''
    size.addEventListener('change', () => { job.size = size.value || null; redraw() })
    fields.append(field('House size', size))
  }
  const byArea = list.method === 'by_area'
  const postcode = el('input', { type: 'text', autocomplete: 'off', 'aria-label': postcodeWords('Postcode') })
  postcode.value = job.postcode
  postcode.addEventListener('input', () => { job.postcode = postcode.value.trim(); redraw() })
  if (!byArea) fields.append(field(postcodeWords('Postcode'), postcode))
  const roof = el('input', { type: 'number', inputmode: 'numeric', min: '0', step: '1', 'aria-label': 'Roof windows' })
  roof.value = job.roof_windows ? String(job.roof_windows) : ''
  roof.addEventListener('input', () => { job.roof_windows = wholeOrNull(roof.value); redraw() })
  const fronts = el('input', { type: 'checkbox' })
  fronts.checked = !!job.fronts_only
  fronts.addEventListener('change', () => { job.fronts_only = fronts.checked; redraw() })
  if (!byArea) fields.append(field('Roof windows (Velux)', roof), el('label', { class: 'check-label' }, fronts, 'Fronts only'))
  // How often, the app's one question: a regular round's weeks price at the nearest of Your prices' bands; Just once
  // and Ad hoc at one-off.
  if (!byArea) {
    const pick = howOftenPicker({ value: often, question: 'How often?', onChange: (v, k) => {
      often = v; oftenK = k
      job.how_often = v ? (v.days ? bandOf(v.days) : 'one_off') : null
      redraw(); drawPrices()
    } })
    fields.append(el('div', { class: 'write-quote-often' }, pick.node))
  }
  jobBox.append(fields)

  // ---- 3. the price, 4. the reply, 5. send ----
  const replyBox = el('section', { class: 'write-quote-reply' })
  const priceBox = el('div', { class: 'write-quote-price', role: 'status' })
  const draft = el('textarea', { rows: '12', 'aria-label': 'Your reply', class: 'write-quote-draft' })
  const redo = el('button', { type: 'button', class: 'link-button' }, 'Write it again from the price')
  const stale = el('p', { class: 'write-quote-stale', role: 'alert' }, 'The job has changed since you changed this reply, so its prices are out of date. Write it again from the price to send it.')
  const sendRow = el('div', { class: 'write-quote-send' })
  const sentRow = el('div', { class: 'write-quote-sent' })
  const note = el('p', { class: 'form-error', role: 'alert' })
  let pickerWarning: string | null = null
  const unknownWarning = (): string => `We couldn't confirm the email went to ${lead.name}. Check with them or your sent emails.`
  const sentBtn = el('button', { type: 'button', class: 'btn-primary' }, "I've sent it: save the quote")
  sentRow.append(sentBtn, el('p', { class: 'muted' }, 'Puts the price and how often on this job, and counts the reply like any other.'))
  const pdfActions = el('div', { class: 'quote-pdf-actions' })
  const pdfResult = el('div', { class: 'quote-pdf-result', 'aria-live': 'polite' })
  replyBox.append(el('h3', {}, 'The price'), priceBox, el('h3', {}, 'Your reply'), draft, stale, redo, pdfActions, pdfResult, sendRow, sentRow, note)
  // ---- step 1: what's the job, as picture tiles ----
  const step1 = el('section', { class: 'write-quote-step' })
  const drawStep1 = (): void => {
    step1.replaceChildren(el('h3', { class: 'write-quote-step-title' }, "What's the job?"), el('p', { class: 'muted' }, 'Tap all that apply.'))
    const tiles = el('div', { class: 'wd-extra-tiles' })
    const tile = (k: Svc, title: string, sub: string, drawing: Drawing, fixed = false): void => {
      const on = services.has(k)
      const b = el('button', { type: 'button', class: `wd-extra-tile${on ? ' is-on' : ''}`, 'aria-pressed': String(on), ...(fixed ? { 'aria-disabled': 'true' } : {}) },
        el('span', { class: 'wd-extra-top' }, el('span', { class: 'booking-extras-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, 18)), ...(on ? [lineDrawing('tick', 18)] : [])),
        el('span', { class: 'wd-extra-words' }, el('strong', {}, title), el('span', {}, sub)))
      b.addEventListener('click', () => { if (fixed) return; if (on) services.delete(k); else services.add(k); drawStep1(); redraw() })
      tiles.append(b)
    }
    if (!byArea) {
      tile('windows', 'Windows', 'Outsides', 'house', true)
    }
    tile('parking', 'Parking or access', 'Worth knowing', 'pin')
    step1.append(tiles)
    if (!byArea) step1.append(picker!.node)
    if (services.has('parking')) {
      const t = el('textarea', { rows: '2', maxlength: '300', class: 'parking-sheet-rest', placeholder: 'e.g. permit only, park in the side road', 'aria-label': 'Parking or access' }) as HTMLTextAreaElement
      t.value = parkingWords
      t.addEventListener('input', () => { parkingWords = t.value })
      step1.append(el('label', { class: 'booking-field' }, 'Parking or access: goes on their page when you book them', t))
    }
  }
  // ---- step 3's prices: every figure a box ----
  const pricesBox = el('div', { class: 'write-quote-prices' })
  function drawPrices(): void {
    const c = !manualOnly && job.how_often ? quote(list, job as Job, currencySymbol(), roundConfig().country) : null
    pricesBox.hidden = manualOnly || usingBreakdown()
    if (editor) editor.node.hidden = !breakdownAllowed()
    pricesBox.replaceChildren()
    if (!c) return
    const box = (label: string, value: string | undefined, calc: number | null, set: (v: string) => void, sub = ''): HTMLElement => {
      const input = el('input', { type: 'text', inputmode: 'decimal', class: 'wd-extra-price', 'aria-label': label }) as HTMLInputElement
      input.value = value ?? (calc === null ? '' : (calc / 100).toFixed(2))
      input.addEventListener('input', () => { set(input.value); redraw() })
      return el('div', { class: 'write-quote-price-row' }, el('span', { class: 'write-quote-price-words' }, el('span', {}, label), ...(sub || calc !== null ? [el('small', { class: 'muted' }, sub || `Your prices: ${quoteAmount(calc!, null)}`)] : [])),
        el('span', { class: 'wd-extra-each' }, el('span', {}, currencySymbol()), input))
    }
    const one = job.how_often === 'one_off' || byArea
    pricesBox.append(box(one ? (oftenK === 'adhoc' ? 'Each time' : 'The clean') : 'First clean', typed.first, c.first_pence, (v) => { typed.first = v }))
    if (!one) pricesBox.append(box(`Then every ${often?.days ? cycleLabel(often.days, null) : 'visit'}`, typed.regular, c.regular_pence, (v) => { typed.regular = v }))
    picker!.refresh()
    if (!byArea) pricesBox.append(picker!.fields)
    pricesBox.append(el('p', { class: 'muted' }, "Type over any price you've agreed differently. The reply changes with it."))
  }
  const step2 = el('section', { class: 'write-quote-step' }, el('h3', { class: 'write-quote-step-title' }, 'The house, and how often'), jobBox)
  const quoteMain = el('div', { class: 'quote-breakdown-main' }, pricesBox, replyBox)
  const quotePreview = el('aside', { class: 'quote-live-preview', 'aria-label': 'Quote preview', hidden: '' })
  const step3 = el('section', { class: 'write-quote-step' }, el('h3', { class: 'write-quote-step-title' }, 'The price, and your reply'), el('div', { class: 'quote-breakdown-layout' }, quoteMain, quotePreview))
  const dots = el('div', { class: 'write-quote-dots', 'aria-hidden': 'true' })
  const back = el('button', { type: 'button', class: 'btn-secondary' }, 'Back')
  const next = el('button', { type: 'button', class: 'btn-primary' }, 'Next')
  const nav = el('div', { class: 'write-quote-nav' }, back, next)
  const go = (n: number): void => {
    step = n
    step1.hidden = n !== 1; step2.hidden = n !== 2; step3.hidden = n !== 3
    back.hidden = n === 1; next.hidden = n === 3
    dots.replaceChildren(...[1, 2, 3].map((i) => el('span', { class: i <= n ? 'is-on' : '' })))
    if (n === 3) { mountBreakdown(); drawPrices(); drawQuotePreview() }
    if (n === 1) picker!.refresh()
    // Each step starts at its top, wherever the last one was scrolled to.
    const dlg = root.closest('dialog')
    dlg?.scrollTo?.({ top: 0 }); dlg?.querySelector('.booking-content')?.scrollTo?.({ top: 0 }); dots.scrollIntoView?.({ block: 'start' })
  }
  back.addEventListener('click', () => go(step - 1))
  next.addEventListener('click', () => {
    if (step === 2 && !job.how_often) { note.textContent = 'Pick how often first.'; return }
    note.textContent = ''
    go(step + 1)
  })
  drawStep1()
  root.append(dots, step1, step2, step3, nav)
  go(manualOnly ? 3 : 1)
  if (manualOnly) {
    dots.hidden = true; nav.hidden = true
  }

  draft.addEventListener('input', () => { edited = true; hooks.edited(true); redo.hidden = false; drawSend(); drawQuotePreview() })
  redo.addEventListener('click', () => { edited = false; hooks.edited(false); redraw() })

  function drawPrice(): void {
    priceBox.textContent = ''
    if (usingBreakdown()) { priceBox.append(el('strong', { class: 'quote-total' }, `Total ${money(breakdownTotal())}`)); return }
    const missing = missingWords(q)
    if (missing || !q) { priceBox.append(el('p', { class: 'write-quote-missing' }, missing ?? '')); return }
    // The figures themselves are boxes above (step 3); here only what the first visit comes to.
    const extra = extrasNow().reduce((n, e) => n + e.count * e.each_pence, 0) + (picker!.has('insides') && q.inside_pence != null ? q.inside_pence : 0)
    if (extra && !q.first_range_pence) priceBox.append(el('div', { class: 'write-quote-row' }, el('span', {}, 'The first visit comes to'), el('strong', {}, quoteAmount(q.first_pence + extra, null))))
    if (q.regular_range_pence || q.first_range_pence) {
      priceBox.append(el('p', { class: 'muted' }, postcodeWords('No postcode in one of your price areas, so the reply gives a range. Add the postcode for one price; a range saves on the job as no price.')))
    }
  }

  // The send buttons carry whatever is in the box now, so a change by hand goes as typed.
  function drawSend(): void {
    sendRow.textContent = ''
    // Out of date: no way to send words whose prices differ from the ones that would be saved.
    const problem = usingBreakdown() ? editor?.problem() ?? (breakdownLines.length ? null : 'Choose the work for this quote.') : picker!.problem()
    const out = isStale() || !!problem
    // A corrected picker clears its own warning, never a send failure or save outcome.
    if (problem) note.textContent = problem
    else if (pickerWarning !== null && note.textContent === pickerWarning) note.textContent = ''
    pickerWarning = problem
    stale.hidden = !out || draft.hidden
    sentRow.hidden = !!lead.quote_send_key || draft.hidden
    sentBtn.disabled = out || busy
    drawPdfActions()
    if (lead.quote_send_key) {
      const live = liveQuoteSend(lead)
      if (outcomeUnknown || !live || lead.quote_send_sent) {
        note.textContent = lead.quote_send_sent ? 'This email already went. Save the quote instead.' : unknownWarning()
        const went = el('button', { type: 'button', class: 'btn-primary' }, 'It went: save the quote')
        went.disabled = busy || live || out || q?.status !== 'priced'
        went.addEventListener('click', () => void resolveOutcome('went'))
        sendRow.append(went)
        if (!lead.quote_send_sent) {
          const no = el('button', { type: 'button', class: 'btn-secondary' }, "It didn't go: let me send again")
          no.disabled = busy || live
          no.addEventListener('click', () => void resolveOutcome('not_sent'))
          sendRow.append(no)
        }
      } else note.textContent = "This job's quote email is going out. Try again in a moment."
      if (live && !claimTimer) {
        const at = new Date(lead.quote_send_at!.replace(' ', 'T') + 'Z').getTime()
        claimTimer = setTimeout(() => { claimTimer = undefined; if (root.isConnected) drawSend() }, Math.max(1, at + 120_001 - Date.now()))
      }
      return
    }
    // No reply yet (a detail still to fill in) or an out-of-date one: no send buttons at all, the same rule
    // as "I've sent it", so an empty or wrong message can never be opened.
    if (out || draft.hidden || !draft.value.trim()) return
    const text = draft.value
    const phone = leadPhoneLink(lead.phone, text, { own: lead.message_by })
    if (!sendIntent) {
      if (phone.href) {
        const way = phone.channel === 'text' ? 'text' : 'whatsapp'
        const a = el('a', { href: phone.href, target: way === 'text' ? '_self' : '_blank', rel: 'noopener', class: 'btn-whatsapp write-quote-link' })
        a.append(wayIcon(way), el('span', {}, wayWords(way).button))
        bindEmailSend(a, () => {
          if (busy || isStale() || !draft.value.trim()) return null
          const href = a.getAttribute('href'), body = onAPhone() ? null : saveBody(phone.channel, false)
          return href ? { href, name: lead.name, sent: () => { channel = phone.channel; sentRow.classList.add('write-quote-sent-now'); if(body)saveManual(body) } } : null
        })
        sendRow.append(a)
      } else {
        sendRow.append(el('p', { class: 'muted' }, `${phone.channel === 'text' ? 'No mobile' : 'No WhatsApp number'} on file.`))
      }
    }
    if (!usingBreakdown() && emailOn && !hasSavedQuote(lead) && !lead.converted_customer_id && getMe()?.role === 'owner' && EMAIL_RULE.test((lead.email ?? '').trim()) && q?.status === 'priced') {
      const send = el('button', { type: 'button', class: 'btn-primary' }, 'Send by email')
      send.disabled = busy
      send.addEventListener('click', () => void saveByEmail())
      sendRow.append(el('div', { class: 'write-quote-email' }, send, el('p', { class: 'muted' }, `Goes from Roundly in ${getMe()?.settings?.business_name ?? ''}'s name. Replies come to ${getMe()?.email ?? ''}.`)))
    }
    if (sendIntent) return
    // Their way first (above); the other phone way too, so a text can go to someone who asked on WhatsApp (Liam, 27 Oct
    // 2026: "theres no option to send it other than email?").
    if (phone.href) {
      const otherWay = phone.channel === 'text' ? 'whatsapp' : 'text'
      const other = leadPhoneLink(lead.phone, text, { own: otherWay })
      if (other.href && other.channel !== phone.channel) {
        const a = el('a', { href: other.href, target: otherWay === 'text' ? '_self' : '_blank', rel: 'noopener', class: 'btn-secondary write-quote-link' })
        a.append(wayIcon(otherWay), el('span', {}, wayWords(otherWay).button))
        bindEmailSend(a, () => {
          if (busy || isStale() || !draft.value.trim()) return null
          const href = a.getAttribute('href'), body = onAPhone() ? null : saveBody(other.channel, false)
          return href ? { href, name: lead.name, sent: () => { channel = other.channel; sentRow.classList.add('write-quote-sent-now'); if(body)saveManual(body) } } : null
        })
        sendRow.append(a)
      }
    }
    const mail = mailtoHref((lead.email ?? '').trim().toLowerCase(), mailSubject('quote'), text)
    if (mail) {
      const a = el('a', { href: mail, class: 'btn-secondary write-quote-link' })
      a.append(wayIcon('email'), el('span', {}, wayWords('email').button))
      bindEmailSend(a, () => {
        if (busy || isStale() || !draft.value.trim()) return null
        const href = mailtoHref((lead.email ?? '').trim().toLowerCase(), mailSubject('quote'), draft.value)
        const body = onAPhone() ? null : saveBody('email', false)
        return href ? { href, sent: () => {
          channel = 'email'
          sentRow.classList.add('write-quote-sent-now')
          // The phone keeps its existing separate "I've sent it" step.
          if (body) saveManual(body)
        } } : null
      })
      sendRow.append(a)
    }
    // Copy, to paste anywhere (Messenger, Instagram). Copied is not sent: "I've sent it" still saves the quote (Astra).
    const copy = el('button', { type: 'button', class: 'btn-secondary write-quote-link' }, 'Copy')
    copy.addEventListener('click', () => {
      void copyText(text, { button: copy, what: 'reply' }).then(copied => { if (copied) sentRow.classList.add('write-quote-sent-now') })
    })
    sendRow.append(copy)
  }

  // The reply the job gives now, or null while a detail the price needs is missing.
  const wordsNow = (): string | null => usingBreakdown() ? (breakdownLines.length ? `Hi ${lead.name.split(' ')[0]},\n\nHere's the quote for the work we talked about. The total is ${money(breakdownTotal())}.\n\nJust reply if you'd like to go ahead.\n\n${getMe()?.settings?.booking_signoff ?? ''}` : null) : (q && job.how_often ? quoteMessage(lead.name, list, job as Job, q, {
    insidesIncluded: picker!.has('insides'), insidesEvery: picker!.value().insides?.every ?? false, adhoc: oftenK === 'adhoc',
    extras: extrasNow().map((e) => ({ name: e.kind === 'conservatory' ? `conservatory roof (${e.count} panel${e.count === 1 ? '' : 's'})` : e.name, pence: e.count * e.each_pence })),
  }) : null)
  const isStale = (): boolean => usingBreakdown() ? !!editor?.problem() || !breakdownLines.length : !!picker!.problem() || replyOutOfDate(edited, builtFrom, wordsNow())

  let replyMode = false
  let ordinaryReply: { value: string; edited: boolean; builtFrom: string | null } | null = null
  let breakdownReply: string | null = oldQuote?.note ?? null

  function redraw(): void {
    const mode = usingBreakdown()
    if (mode !== replyMode) {
      if (mode) {
        ordinaryReply = { value: draft.value, edited, builtFrom }
        edited = breakdownReply !== null
        if (breakdownReply !== null) draft.value = breakdownReply
      } else {
        breakdownReply = draft.value
        edited = ordinaryReply?.edited ?? false
        builtFrom = ordinaryReply?.builtFrom ?? null
        draft.value = ordinaryReply?.value ?? ''
      }
      replyMode = mode
    }
    q = priced()
    drawPrice()
    const words = wordsNow()
    const ready = words !== null
    draft.hidden = !ready
    sendRow.hidden = !ready && !lead.quote_send_key
    sentRow.hidden = !ready
    redo.hidden = !ready || !edited
    if (ready && !edited) { draft.value = words; builtFrom = words }
    drawSend()
    drawQuotePreview()
    if (editor) editor.node.hidden = !breakdownAllowed()
    pricesBox.hidden = manualOnly || usingBreakdown()
  }

  function saveBody(way: NoteChannel = channel, remember = true): Record<string, unknown> {
    const today = todayISO()
    const n = enquiryMessages(lead) + 1
    const line = appendNote(lead.contact_note, enquiryNoteLine(noteDate(today), n, way))
    const fields0 = usingBreakdown() ? { cycle_weeks: null, regular_pence: lead.regular_pence, initial_pence: breakdownTotal() } : quoteSaveFields(job as Job, q!)
    if (remember) picker!.remember()
    const body: Record<string, unknown> = {
      version: lead.version,
      ...fields0,
      // The exact weeks picked (3 weeks prices as 4 weeks' band, but books every 3 weeks), and Just once / Ad hoc (0092).
      ...(often?.days && often.days % 7 === 0 ? { cycle_weeks: often.days / 7 } : {}),
      cycle_text: oftenK === 'once' ? 'Just once' : oftenK === 'adhoc' ? 'Ad-hoc' : null,
      quote_json: JSON.stringify({
        v: 1, ...(usingBreakdown() ? { whole_breakdown: true } : {}), services: [...services, ...(['insides', 'gutters', 'conservatory', 'other'] as const).filter(k => picker!.has(k))], windows: job.windows ?? null, roof_windows: job.roof_windows ?? null, fronts_only: !!job.fronts_only,
        size: job.size ?? null, sqm: job.sqm ?? null, postcode: job.postcode || null, cycle_days: often?.days ?? null,
        insides: !usingBreakdown() && picker!.has('insides') && q!.inside_pence != null ? { pence: q!.inside_pence, every: picker!.value().insides?.every && oftenK === 'regular' } : null,
        extras: usingBreakdown() ? [] : extrasNow(), parking: services.has('parking') && parkingWords.trim() ? parkingWords.trim() : null,
        first_pence: usingBreakdown() ? breakdownTotal() : q!.first_pence, regular_pence: usingBreakdown() ? fields0.regular_pence : q!.regular_pence,
      }),
      // The same marking a reply on Messages makes (rebook.ts recordEnquiryMessage).
      enquiry_messages: n,
      enquiry_messaged_on: today,
      last_contacted_on: today,
    }
    // Left out when the line would not fit: the owner's own note must survive.
    if (line !== (lead.contact_note ?? '')) body.contact_note = line
    // Marked as quoted: a new enquiry moves to the board's Quoted column (the status New jobs' own Quoted
    // column sets). Any other status the owner chose is left as it is.
    const status = quotedStatus(lead.status_raw)
    if (status) body.status_raw = status
    return body
  }

  async function saveByEmail(): Promise<void> {
    if (busy || !q || q.status !== 'priced' || isStale() || !draft.value.trim()) return
    const path = `/api/leads/${lead.id}`
    if (getQueue().some(e => e.path === path)) { note.textContent = 'This job has a change waiting on New jobs. Sort that out first.'; return }
    if (lead.quote_send_key) return
    note.textContent = ''
    // The newest message from them this phone had seen: the quote going out reads up to it in Chats, never past it (tidy-up
    // item 9, Sonnet's edge case S E1, 20 Oct 2026).
    if (!sendIntent) sendIntent = { key: crypto.randomUUID(), body: { ...saveBody('email'), text: draft.value, ...(lead.last_in_id ? { upto: lead.last_in_id } : {}) } }
    busy = true; hooks.saving(true); drawSend()
    draft.disabled = true
    for (const input of fields.querySelectorAll('input, select')) (input as HTMLInputElement).disabled = true
    redo.disabled = true
    let ack: Ack<Lead>
    try {
      const data = await changeNow<{ row?: Lead; current?: Lead; code?: string; error?: string }>('POST', `${path}/send-quote`, sendIntent.body, sendIntent.key)
      if (data.current?.id === lead.id) lead = data.current
      ack = { ok: !!data.row, status: 200, row: data.row, code: data.code, error: data.error }
    } catch (err) {
      const current = err instanceof ApiError ? err.body?.current as Lead | undefined : undefined
      if (current?.id === lead.id) lead = current
      ack = { ok: false, status: err instanceof ApiError ? err.status : 0, code: err instanceof ApiError ? err.code : undefined, error: (err as Error).message }
    }
    busy = false; hooks.saving(false); draft.disabled = false
    if (ack.ok) { toast(`Quote saved on ${lead.name}'s job`); hooks.done(); return }
    // An unconfirmed request keeps its key and exact text for a safe retry.
    if (ack.code === 'send_refused') sendIntent = null
    if (ack.code === 'version_conflict' || ack.code === 'send_unknown' || ack.code === 'quote_not_saved' || ack.status === 0) {
      try { lead = (await api<{ lead: Lead }>(path)).lead } catch { /* Keep the reply for another try. */ }
    }
    outcomeUnknown = ack.code === 'send_unknown' || ack.status === 0
    note.textContent = lead.quote_send_key && !liveQuoteSend(lead) ? unknownWarning() : ack.status === 0 ? "We couldn't confirm the email went. Don't send it again yet; check in 2 minutes." : ack.error ?? "We couldn't confirm whether the email went. Check with the customer before sending again."
    for (const input of fields.querySelectorAll('input, select')) (input as HTMLInputElement).disabled = !!sendIntent
    draft.readOnly = !!sendIntent
    redo.disabled = !!sendIntent
    drawSend()
  }

  async function resolveOutcome(resolve: 'went' | 'not_sent'): Promise<void> {
    if (busy || !lead.quote_send_key || liveQuoteSend(lead)) return
    if (resolve === 'went' && (!q || q.status !== 'priced' || isStale())) return
    busy = true; hooks.saving(true); drawSend()
    try {
      const body = { ...(resolve === 'went' ? sendIntent?.body ?? saveBody('email') : { version: lead.version }), version: lead.version, resolve }
      const data = await changeNow<{ row: Lead }>('POST', `/api/leads/${lead.id}/send-outcome`, body)
      lead = data.row
      sendIntent = null; outcomeUnknown = false
      if (resolve === 'went') { toast(`Quote saved on ${lead.name}'s job`); hooks.done(); return }
      note.textContent = ''
      draft.readOnly = false; redo.disabled = false
      for (const input of fields.querySelectorAll('input, select')) (input as HTMLInputElement).disabled = false
    } catch (err) {
      note.textContent = (err as Error).message
      try { lead = (await api<{ lead: Lead }>(`/api/leads/${lead.id}`)).lead } catch { /* Keep the draft. */ }
    } finally { busy = false; root.inert = false; hooks.saving(false); drawSend() }
  }

  function quoteTilePrices() {
    const rate = windowRate(list, job as Job, roundConfig().country).rate
    return { ...(rate > 0 ? { windows: rate } : {}),
      ...(list.roof_window_pence != null ? { roof: list.roof_window_pence } : rate > 0 ? { roof: rate } : {}) }
  }
  function mountBreakdown(): void {
    if (editor) { editor.setPrices(quoteTilePrices()); return }
    editor = invoiceBreakdown([{ id: 0, version: 1, date: todayISO(), label: 'The work', amount_pence: 0, paid_pence: 0, money_rev: 0, lines: breakdownLines, fixed: [], plain_lines: [] }], quoteTilePrices(), { draft: true, onChange: () => { if (usingBreakdown()) { hooks.edited(edited || !!editor?.dirty()); drawSend() } }, onSaved: () => {}, onLocked: () => {},
      onToggle: open => {
        breakdownOpen = open
        issued = null; redraw(); drawPrices(); hooks.edited(edited || !!editor?.dirty())
      },
      onPreview: lines => {
        const previous = breakdownTotal()
        breakdownLines = lines.get(0) ?? []
        if (usingBreakdown()) {
          if (edited && previous !== breakdownTotal()) {
            const old = money(previous), fresh = money(breakdownTotal())
            const totalWords = /(\btotal(?:\s+is|\s*:)?\s+)[£$]\d[\d,]*(?:\.\d{1,2})?/i
            if (totalWords.test(draft.value)) draft.value = draft.value.replace(totalWords, (_, label: string) => label + fresh)
            else if (draft.value.split(old).length === 2) draft.value = draft.value.replace(old, () => fresh)
            else draft.value += `\n\nTotal: ${fresh}`
          }
          issued = null; redraw()
        }
      },
    })
    quoteMain.insertBefore(editor.node, replyBox)
    editor.node.hidden = !breakdownAllowed()
    void api<{ row: typeof previewSetup }>('/api/business/invoices').then(r => { previewSetup = r.row; drawQuotePreview() }).catch(() => {})
  }
  function drawQuotePreview(): void {
    quotePreview.hidden = !usingBreakdown()
    step3.classList.toggle('quote-breakdown-open', usingBreakdown())
    root.closest('dialog')?.classList.toggle('quote-breakdown-wide', usingBreakdown())
    quotePreview.replaceChildren()
    if (!usingBreakdown()) return
    const from = previewSetup.business_name ?? getMe()?.settings?.business_name ?? ''
    const number = previewNumber ? `Q-${String(previewNumber).padStart(4, '0')}` : 'Number added when the PDF is made'
    const expires = new Date(`${todayISO()}T12:00:00Z`); expires.setUTCDate(expires.getUTCDate() + 30)
    quotePreview.append(el('h2', {}, 'QUOTE'), el('p', { class: 'muted' }, number), el('p', {}, `Date ${shortDate(todayISO())} · Valid until ${shortDate(expires.toISOString().slice(0, 10))}`),
      el('strong', {}, from), el('p', {}, [previewSetup.address, previewSetup.contact].filter(Boolean).join('\n')),
      el('strong', {}, lead.name), el('p', {}, lead.address ?? ''))
    const table = el('table', { class: 'quote-preview-lines' }, el('thead', {}, el('tr', {}, ...['What', 'How many', 'Each', 'Price'].map(t => el('th', {}, t)))))
    const rows = el('tbody')
    for (const l of breakdownLines) rows.append(el('tr', {}, el('td', {}, l.name), el('td', {}, String(l.count)), el('td', {}, money(l.each_pence)), el('td', {}, money(l.count * l.each_pence))))
    table.append(rows)
    quotePreview.append(table, el('strong', { class: 'quote-preview-total' }, `Total ${money(breakdownTotal())}`))
    if (previewSetup.vat_on === true && roundConfig().country === 'GB') quotePreview.append(el('p', {}, 'Prices include VAT.'))
    quotePreview.append(el('p', { class: 'quote-preview-note' }, draft.value), el('p', { class: 'muted' }, 'This is a quote, not a bill. Nothing to pay until the work is done.'))
  }
  const pdfProblem = (): boolean => busy || isStale() || breakdownTotal() < 1 || !draft.value.trim()
  function drawPdfActions(): void {
    pdfActions.replaceChildren(); pdfActions.hidden = !usingBreakdown()
    sentBtn.className = usingBreakdown() ? 'btn-secondary' : 'btn-primary'
    if (!usingBreakdown()) return
    for (const [label, mode] of [['Send as a PDF quote', 'share'], ['Download PDF', 'download'], ['Email it', 'email']] as const) {
      const button = el('button', { type: 'button', class: mode === 'share' ? 'btn-primary' : 'btn-secondary' }, label) as HTMLButtonElement
      button.disabled = pdfProblem() || (mode === 'email' && !EMAIL_RULE.test((lead.email ?? '').trim()))
      button.addEventListener('click', () => void preparePdf(mode)); pdfActions.append(button)
    }
    pdfActions.append(el('p', { class: 'muted' }, 'Email it puts a link to the quote in your email app. It does not attach the PDF.'))
    sentBtn.className = 'btn-secondary'
  }
  async function freezeQuote(): Promise<LeadQuote> {
    const fields = saveBody()
    const save = Object.fromEntries(['cycle_weeks','cycle_text','quote_json','regular_pence','initial_pence'].map(k => [k, fields[k]]))
    const body = { version: lead.version, lines: breakdownLines, note: draft.value, save }
    const fingerprint = JSON.stringify(body)
    if (issued?.fingerprint === fingerprint) return issued.row
    if (!quoteIntent || quoteIntent.fingerprint !== fingerprint) quoteIntent = { key: crypto.randomUUID(), fingerprint }
    const response = await changeNow<{ row: LeadQuote }>('POST', `/api/leads/${lead.id}/quotes`, body, quoteIntent.key)
    issued = { row: response.row, fingerprint }; quoteIntent = null; previewNumber = response.row.number; drawQuotePreview()
    return response.row
  }
  async function confirmQuote(way: string, frozen?: LeadQuote, sentText?: string): Promise<void> {
    if (busy || (!frozen && pdfProblem())) return
    busy = true; root.inert = true; hooks.saving(true); drawSend(); note.textContent = ''
    try {
      const quote = frozen ?? await freezeQuote()
      const body = { version: lead.version, way, upto: lead.last_in_id ?? 0, sent_text: sentText ?? (frozen ? quote.note : draft.value) }
      const fingerprint = JSON.stringify({ quote: quote.id, ...body })
      if (!confirmIntent || confirmIntent.body !== fingerprint) confirmIntent = { key: crypto.randomUUID(), body: fingerprint }
      await changeNow('POST', `/api/lead-quotes/${quote.id}`, body, confirmIntent.key)
      window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      toast(`Quote saved on ${lead.name}'s job`); hooks.done()
    } catch (e) { note.textContent = e instanceof Error ? e.message : 'The quote did not save. Try again.' }
    finally { busy = false; root.inert = false; hooks.saving(false); drawSend() }
  }
  async function preparePdf(mode: 'share' | 'download' | 'email'): Promise<void> {
    if (pdfProblem()) return
    busy = true; root.inert = true; hooks.saving(true); drawSend(); pdfResult.replaceChildren(); note.textContent = ''
    try {
      const row = await freezeQuote()
      const number = `Q-${String(row.number).padStart(4, '0')}`
      const link = `${window.location.origin}/quote/${row.token}`
      const ask = (): void => {
        const yes = el('button', { type: 'button', class: 'btn-secondary' }, "I've sent it") as HTMLButtonElement
        yes.addEventListener('click', () => void confirmQuote(mode, row, mode === 'email' ? `${row.note}\n\nYour quote: ${link}` : row.note))
        pdfResult.append(el('p', {}, `Did ${number} go to ${lead.name}?`), yes)
      }
      pdfResult.append(el('p', {}, `${number} · Revision ${row.revision}. Not sent yet.`))
      if (mode === 'email') {
        const href = mailtoHref((lead.email ?? '').trim(), mailSubject('quote'), `${row.note}\n\nYour quote: ${link}`)
        if (!href) throw new Error('Add their email address on New jobs first.')
        const email = el('a', { href, class: 'btn-secondary' }, 'Open email with quote link')
        bindEmailSend(email, () => ({ href, name: lead.name, sent: () => {
          if (onAPhone()) ask()
          else void confirmQuote('email', row, `${row.note}\n\nYour quote: ${link}`)
        } }))
        pdfResult.append(email)
      } else {
        const file = new File([await apiBlob(`/api/lead-quotes/${row.id}?pdf`)], `${number}-r${row.revision}.pdf`, { type: 'application/pdf' })
        const href = URL.createObjectURL(file)
        const download = el('a', { href, download: file.name, class: 'btn-secondary' }, 'Save the PDF')
        download.addEventListener('click', ask, { once: true })
        openPanel?.addEventListener('close', () => URL.revokeObjectURL(href), { once: true })
        if (mode === 'share' && navigator.canShare?.({ files: [file] })) {
          const send = el('button', { type: 'button', class: 'btn-primary' }, 'Send PDF')
          send.addEventListener('click', () => { void navigator.share({ files: [file], title: `Quote ${number}`, text: row.note }).then(ask, e => { if (e?.name !== 'AbortError') note.textContent = "Couldn't share it. Save the PDF and send it your own way." }) })
          pdfResult.append(send)
        } else if (mode === 'share') pdfResult.append(el('p', {}, 'Save the PDF, then attach it in your message.'))
        pdfResult.append(download)
      }
    } catch (e) { note.textContent = e instanceof Error ? e.message : "Couldn't make the PDF. Try again." }
    finally { busy = false; root.inert = false; hooks.saving(false); drawSend() }
  }

  // ---- saving: only once the owner says it went ----
  sentBtn.addEventListener('click', () => {
    if (usingBreakdown()) { void confirmQuote(channel === 'WhatsApp' ? 'whatsapp' : channel); return }
    saveManual()
  })
  function saveManual(snapshot?: Record<string, unknown>): void {
    if (busy || lead.quote_send_key || (!snapshot && (!q || !job.how_often || q.status !== 'priced' || !draft.value.trim() || isStale()))) return
    if (snapshot) picker!.remember()
    note.textContent = ''
    const back = (): void => { busy = false; hooks.saving(false); sentBtn.disabled = false }
    hooks.saving(true)
    busy = true
    sentBtn.disabled = true
    const body = snapshot ?? saveBody()
    // A parked save on this job would hold this one behind it for ever. This panel's own earlier quote saves
    // are replaced by the quote on screen; any other change is the owner's, sorted out on New jobs, never
    // thrown away from here (Astra, 1 Oct).
    const path = `/api/leads/${lead.id}`
    const parked = parkedOnJob(getQueue(), path)
    if (parked.others) {
      back()
      note.textContent = 'This job has another change waiting to be sorted out on New jobs. Sort that out first, then save the quote.'
      return
    }
    for (const key of parked.ours) dismiss(key)
    let finished = false
    const finish = (message: string): void => { finished = true; hooks.saving(false); toast(message); hooks.done() }
    void mutate({
      label: `${QUOTE_LABEL}${lead.name}`,
      method: 'PATCH',
      path,
      body,
      onAck: () => finish(`Quote saved on ${lead.name}'s job`),
      onQueued: () => finish('No signal. Saved on this phone to send later.'),
      onConflict: () => {
        // Only this save's own parked entry: it is replaced when the owner presses again.
        for (const key of parkedOnJob(getQueue(), path).ours) dismiss(key)
        // Take the newest copy of the job, so pressing again saves on top of the other change.
        void api<{ lead: Lead }>(path).then((r) => {
          if (r.lead?.id === lead.id) lead = r.lead
          back()
          note.textContent = 'This job was changed somewhere else meanwhile. Check the reply, then press the button again.'
        }).catch(() => {
          back()
          note.textContent = "This job was changed somewhere else, and the newest copy couldn't load. Close this and try again."
        })
      },
    }).then((ack) => {
      if (finished || ack.ok || ack.status === 409) return
      back()
      note.textContent = ack.error ?? "The quote didn't save."
    })
  }

  if (manualOnly) editor!.node.querySelector('button')!.click()
  redraw()
  // Said up front too, so the owner knows before writing the reply.
  if (parkedOnJob(getQueue(), `/api/leads/${lead.id}`).others) note.textContent = 'This job has another change waiting to be sorted out on New jobs. Sort that out first, then save the quote.'
}
